import { useState, useEffect } from "react";
import { storageGet, storageSet, devWarn, ONBOARDING_DISMISSED_KEY } from "@/lib/uiStorage";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Target, Layers, Users, Zap, X, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Allow-list must match backend ALLOWED_GOALS in routes/onboarding.py.
const GOAL_OPTIONS = [
  { id: "increase_roas",        label: "Increase ROAS",                hint: "Get more return per dollar of ad spend.", icon: TrendingUp, gradient: "from-violet-500 to-indigo-600" },
  { id: "lower_cpa",            label: "Lower my CPA",                 hint: "Spend less to acquire each customer.",   icon: Target,     gradient: "from-cyan-500 to-blue-600" },
  { id: "scale_creative",       label: "Scale creative production",    hint: "Launch many more ad variants per month.",icon: Layers,     gradient: "from-violet-600 to-indigo-600" },
  { id: "reduce_creator_costs", label: "Stop paying UGC creators",     hint: "Replace external creators with AI.",     icon: Users,      gradient: "from-fuchsia-500 to-pink-600" },
  { id: "test_faster",          label: "Test creatives faster",        hint: "Iterate weekly, not monthly.",           icon: Zap,        gradient: "from-violet-500 to-fuchsia-600" },
];

/**
 * Shows on first authenticated mount if user.onboarding_goal is unset.
 * Saves to backend on selection. Skippable. Single-shot via localStorage
 * `lumora_onboarding_dismissed_v1` so users who skip aren't re-prompted
 * in the same session (we still re-prompt on next login if unset).
 */
/** Opens once per session for signed-in users who haven't picked a goal yet. */
function useOnboardingGoal(user) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) return;
    // The guided first-ad wizard + verification screens own their own flow.
    if (/^\/(onboarding|verify-email|auth)/.test(window.location.pathname)) return;
    if (storageGet(ONBOARDING_DISMISSED_KEY, { session: true })) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/onboarding-goal`);
        if (!cancelled && !r.data?.goal) setOpen(true);
      } catch (err) { devWarn("Onboarding goal lookup failed (best-effort)", err); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = () => { storageSet(ONBOARDING_DISMISSED_KEY, "1", { session: true }); setOpen(false); };

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/onboarding-goal`, { goal: selected });
      toast.success("Got it — we'll tailor your experience.");
      dismiss();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save your goal. Skipping.");
      dismiss();
    } finally { setSubmitting(false); }
  };

  return { open, submitting, selected, setSelected, dismiss, submit };
}

const GoalOption = ({ goal: g, active, onSelect }) => {
  const Icon = g.icon;
  return (
    <button onClick={() => onSelect(g.id)} data-testid={`onboarding-goal-${g.id}`}
      className={`text-left rounded-2xl p-4 ring-1 transition-all flex items-start gap-3 ${active ? "ring-violet-400/60 bg-violet-500/[0.08]" : "ring-white/8 bg-white/[0.03] hover:bg-white/[0.05] hover:ring-white/15"}`}>
      <div className={`shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${g.gradient} flex items-center justify-center`}><Icon className="w-4 h-4 text-white" /></div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-white">{g.label}</p>
        <p className="text-xs text-white/45 mt-0.5">{g.hint}</p>
      </div>
    </button>
  );
};

const ModalHeader = () => (
  <div className="mb-6">
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full pill text-xs text-violet-300 mb-3"><Target className="w-3 h-3" /> 30 seconds · personalizes your workspace</span>
    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2" data-testid="onboarding-title">What&apos;s your #1 goal with Lumora?</h2>
    <p className="text-sm text-white/55">We&apos;ll tune the studio, prompts, and analytics around this outcome.</p>
  </div>
);

const ModalFooter = ({ onSkip, onSubmit, disabled, submitting }) => (
  <div className="flex items-center justify-between gap-3">
    <button onClick={onSkip} className="text-xs text-white/40 hover:text-white/60 transition-colors" data-testid="onboarding-skip">Skip for now</button>
    <Button onClick={onSubmit} disabled={disabled} data-testid="onboarding-submit"
      className="rounded-full px-6 py-5 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white font-bold text-sm shadow-lg disabled:opacity-50">
      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-1.5 w-4 h-4" /></>}
    </Button>
  </div>
);

export function OnboardingGoalModal() {
  const { user } = useAuth();
  const { open, submitting, selected, setSelected, dismiss, submit } = useOnboardingGoal(user);
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="onboarding-goal-modal"
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }} transition={{ duration: 0.18 }}
            className="relative w-full max-w-2xl rounded-3xl bg-[#0f0f22] ring-1 ring-white/10 shadow-2xl p-6 md:p-8">
            <button onClick={dismiss} aria-label="Dismiss" data-testid="onboarding-dismiss"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.05] hover:bg-white/10 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-white/70" />
            </button>
            <ModalHeader />
            <div className="grid sm:grid-cols-2 gap-2.5 mb-6" data-testid="onboarding-options">
              {GOAL_OPTIONS.map((g) => <GoalOption key={g.id} goal={g} active={selected === g.id} onSelect={setSelected} />)}
            </div>
            <ModalFooter onSkip={dismiss} onSubmit={submit} disabled={!selected || submitting} submitting={submitting} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
